import { isDeleted, pageMeta, withTrashedPagesLast } from "./pageMetadata";
import type { Database } from "../../lib/database.types";
import { pageRepository } from "./pageRepository";
import { slugOrFallback } from "./pageNaming";

type PageInsert = Database["public"]["Tables"]["pages"]["Insert"];
type PageUpdate = Database["public"]["Tables"]["pages"]["Update"];

// A title with no Latin letters would otherwise leave an empty slug.
const normalizeSlug = (slug: string) => slugOrFallback(slug);

export const pageService = {
  list: async (siteVersionId: string) =>
    (await pageRepository.list(siteVersionId)).filter((p) => !isDeleted(p)),
  listTrash: async (siteVersionId: string) =>
    (await pageRepository.list(siteVersionId)).filter(isDeleted),
  trash: (page: Database["public"]["Tables"]["pages"]["Row"]) =>
    pageRepository.update(page.id, {
      seo: { ...pageMeta(page), deletedAt: new Date().toISOString() },
    }),
  restore: (page: Database["public"]["Tables"]["pages"]["Row"]) =>
    pageRepository.update(page.id, {
      seo: { ...pageMeta(page), deletedAt: null },
    }),
  create: (values: PageInsert) =>
    pageRepository.create({
      ...values,
      title: values.title.trim(),
      slug: normalizeSlug(values.slug),
    }),
  update: (pageId: string, values: PageUpdate) =>
    pageRepository.update(pageId, {
      ...values,
      title: values.title?.trim(),
      slug: values.slug === undefined ? undefined : normalizeSlug(values.slug),
    }),
  remove: (pageId: string) => pageRepository.remove(pageId),
  duplicate: (pageId: string, title: string, slug: string) =>
    pageRepository.duplicate(pageId, title.trim(), normalizeSlug(slug)),
  reorder: async (siteVersionId: string, pageIds: string[]) =>
    pageRepository.reorder(
      siteVersionId,
      withTrashedPagesLast(pageIds, await pageRepository.list(siteVersionId)),
    ),
  setHome: (pageId: string) => pageRepository.setHome(pageId),
};
